import Modal from '../common/Modal'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import formatEuro from '../../helpers/formatEuro'

/**
 * @typedef {import('../../helpers/participationHelpers/getComponentParameterBlocks').ParameterBlock} ParameterBlock
 * @typedef {import('../../helpers/participationHelpers/getComponentParameterBlocks').ParticipationSource} ParticipationSource
 */

const formatRate = (rate) =>
    `${new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(rate * 100)}%`

/**
 * Renders a parameter value according to its block kind: a single amount, an
 * amount-per-hour, a label→amount map, the tax brackets or the opleiding×urenband
 * matrix. The tabular kinds use the shared Table component.
 * @param {ParameterBlock} block
 * @returns {JSX.Element}
 */
const renderValue = (block) => {
    if (block.kind === 'amount') {
        return <span className='participation-source-amount'>{formatEuro(block.value)}</span>
    }

    if (block.kind === 'amountPerHour') {
        return <span className='participation-source-amount'>{formatEuro(block.value)} per uur</span>
    }

    if (block.kind === 'amountMap') {
        const items = Object.entries(block.value).map(([label, amount]) => ({ label, amount }))
        return (
            <Table
                columns={[{ key: 'type', label: 'Type' }, { key: 'amount', label: block.amountLabel || 'Bedrag per jaar' }]}
                items={items}
                getItemKey={(item) => item.label}
                showSearch={false}
                renderRow={(item) => (
                    <>
                        <TableCell>{item.label}</TableCell>
                        <TableCell>{formatEuro(item.amount)}</TableCell>
                    </>
                )}
            />
        )
    }

    if (block.kind === 'rateMap') {
        const items = Object.entries(block.value).map(([label, rate]) => ({ label, rate }))
        return (
            <Table
                columns={[{ key: 'type', label: 'Type' }, { key: 'rate', label: 'Percentage' }]}
                items={items}
                getItemKey={(item) => item.label}
                showSearch={false}
                renderRow={(item) => (
                    <>
                        <TableCell>{item.label}</TableCell>
                        <TableCell>{formatRate(item.rate)}</TableCell>
                    </>
                )}
            />
        )
    }

    if (block.kind === 'brackets') {
        const items = block.value.map((bracket, index) => ({ index, ...bracket }))
        return (
            <Table
                columns={[{ key: 'range', label: 'Inkomen' }, { key: 'rate', label: 'Tarief' }]}
                items={items}
                getItemKey={(item) => String(item.index)}
                showSearch={false}
                renderRow={(item) => (
                    <>
                        <TableCell>{Number.isFinite(item.upTo) ? `tot ${formatEuro(item.upTo)}` : 'en hoger'}</TableCell>
                        <TableCell>{formatRate(item.rate)}</TableCell>
                    </>
                )}
            />
        )
    }

    // matrix: education levels (rows) × hours bands (columns)
    const levels = Object.keys(block.value)
    const bands = levels.length ? Object.keys(block.value[levels[0]]) : []
    return (
        <Table
            columns={[{ key: 'level', label: 'Opleidingsniveau' }, ...bands.map((band) => ({ key: band, label: band }))]}
            items={levels.map((level) => ({ level, amounts: block.value[level] }))}
            getItemKey={(item) => item.level}
            showSearch={false}
            renderRow={(item) => (
                <>
                    <TableCell>{item.level}</TableCell>
                    {bands.map((band) => (
                        <TableCell key={band}>{formatEuro(item.amounts[band])}</TableCell>
                    ))}
                </>
            )}
        />
    )
}

/**
 * Lists one source citation.
 * @param {object} props
 * @param {ParticipationSource} props.source
 * @returns {JSX.Element}
 */
const SourceItem = ({ source }) => (
    <div className='participation-source'>
        <span className='participation-source-title'>{source.title}</span>
        {(source.publisher || source.year) && (
            <span className='participation-source-meta'>{[source.publisher, source.year].filter(Boolean).join(' · ')}</span>
        )}
        {source.note && <span className='participation-source-note'>{source.note}</span>}
        {source.url && (
            <a className='participation-source-link' href={source.url} target='_blank' rel='noopener noreferrer'>
                Bekijk bron →
            </a>
        )}
    </div>
)

/**
 * Modal showing the onderbouwing of one onderdeel of the maatschappelijke
 * waarde: the parameter figures used in the berekening and the bronnen they are
 * based on. Open while `componentLabel` is set; closing clears it via `onClose`.
 * Uses the shared Modal.jsx.
 *
 * @param {object} props
 * @param {string|null} props.componentLabel - label of the clicked onderdeel, null when closed
 * @param {Array<ParameterBlock>} props.blocks - figure-and-bronnen blocks to show
 * @param {() => void} props.onClose - called to close the modal
 */
const ParticipationSourcesModal = ({ componentLabel, blocks, onClose }) => {
    const content = (
        <div className='participation-sources'>
            {blocks.length === 0 && (
                <p className='participation-sources-empty'>Voor dit onderdeel is nog geen onderbouwing beschikbaar.</p>
            )}
            {blocks.map((block) => (
                <div key={block.paramKey} className='participation-source-block'>
                    <span className='participation-source-block-label'>{block.label}</span>
                    {renderValue(block)}
                    {block.sources.length > 0 && (
                        <div className='participation-source-list'>
                            {block.sources.map((source) => (
                                <SourceItem key={source.id} source={source} />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )

    return (
        <Modal
            modalOpen={!!componentLabel}
            setModalOpen={onClose}
            pageHeader={componentLabel || 'Onderbouwing'}
            description='De gebruikte rekenbedragen en de bronnen waarop ze gebaseerd zijn.'
            content={content}
            hideConfirm
            cancelLabel='Sluiten'
        />
    )
}

export default ParticipationSourcesModal
